<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :module-enabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :inputs="inputs"
    :is-custom-saving="isSaving"
    @save="save"
  >
    <template v-for="input of inputs" #[`${input.name}.input`]="data">
      <v-col cols="3" :key="input.name">
        <data-input v-model="params[input.name]" v-bind="data" />
      </v-col>
    </template>
    <template #paih_layers.input="data">
      <v-col cols="3">
        <data-input v-model="params.paih_layers" v-bind="data">
          <template #prepend-item>
            <v-list-item @click="toggleSelectAll">
              <v-list-item-action>
                <v-icon :color="params.paih_layers.length > 0 ? 'primary' : ''"> {{ iconSelectAll }} </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title> {{ $i18n.t('button.selectAllItems') }} </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider />
          </template>
          <template #selection="{ item, index }">
            <v-chip v-if="index < 4" close @click:close="params.paih_layers.splice(index, 1)">
              <span class="d-inline-block text-truncate">{{ item.text }}</span>
            </v-chip>
            <span v-if="index === 4" class="text-caption grey--text">
              {{ `(+${$i18n.tc('admin.other', params.paih_layers.length - 4)})` }}
            </span>
          </template>
        </data-input>
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'PaihMigratorSettings',
  components: { AdminWrapper, DataInput },
  data: () => ({
    isFormFetched: false,
    isSaving: false,
    params: {
      paih_smart_match_offers_profile: null,
      paih_complex_geom_datasource_name: '',
      paih_layers: [],
    },
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules@PAIH_MIGRATOR_MODULE.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@paih_migrator_module_enabled'),
    settingsValues: get('admin/settingsValues'),
    moduleEnabler() {
      return {
        name: 'paih_migrator_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    smartMatch: get('smartMatch/profiles'),
    smartMatchArray() {
      return this.$_objectToArray(this.smartMatch).map(smartMatch => {
        return { value: smartMatch.id, text: smartMatch.name };
      });
    },
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources).map(dataSource => {
        return { value: dataSource.name, text: dataSource.verbose_name };
      });
    },
    smartMatchLayersArray() {
      return this.$_objectToArray(this.layers).map(layer => {
        return { value: layer.id, text: layer.name };
      });
    },
    inputs() {
      return [
        {
          name: 'paih_smart_match_offers_profile',
          translationPath: 'admin.paih.smartMatchOffer',
          subtitle: 'admin.paih.smartMatchOfferSubtitle',
          dataType: { name: 'select' },
          items: this.smartMatchArray,
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'paih_complex_geom_datasource_name',
          translationPath: 'admin.paih.geomDatasourceName',
          subtitle: 'admin.paih.geomDatasourceNameSubtitle',
          dataType: { name: 'select' },
          items: this.dataSourcesArray,
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'paih_layers',
          translationPath: 'admin.paih.paihLayers',
          subtitle: 'admin.paih.paihLayersSubtitle',
          dataType: { name: 'select' },
          items: this.smartMatchLayersArray,
          disabled: !this.params.paih_smart_match_offers_profile,
          chips: true,
          multiple: true,
          autoConvertSelect: false,
          rules: [v => v.length > 0 || 'rules.required'],
        },
      ];
    },
    iconSelectAll() {
      if (this.params.paih_layers.length === this.smartMatchLayersArray.length) return 'mdi-checkbox-marked';
      else if (this.params.paih_layers.length) return 'mdi-minus-box';
      else return 'mdi-checkbox-blank-outline';
    },
  },
  watch: {
    isModuleEnabled: {
      handler(nV) {
        if (nV) this.init();
        else this.isFormFetched = true;
      },
      immediate: true,
    },
    'params.paih_smart_match_offers_profile': {
      handler(nV, oV) {
        if (nV && !!oV) this.params.paih_layers = [];
      },
    },
  },
  methods: {
    getDataSources: call('layers/getLayersMetadata'),
    getProfiles: call('smartMatch/getProfiles'),
    editSingleSetting: call('admin/editSingleSetting'),
    toggleSelectAll() {
      if (this.params.paih_layers.length === this.smartMatchLayersArray.length) this.params.paih_layers = [];
      else this.params.paih_layers = this.smartMatchLayersArray.map(smartMatch => smartMatch.value);
    },
    async init() {
      this.isFormFetched = false;
      await Promise.all([this.getDataSources({ with_attributes_schema: false }), this.getProfiles()]);
      for (const param in this.params) {
        this.params[param] = this.settingsValues[param];
      }
      this.isFormFetched = true;
    },
    async save() {
      this.isSaving = true;
      try {
        await Promise.all(
          this.inputs.map(input => {
            return this.editSingleSetting({ key: input.name, body: { value: this.params[input.name] } });
          })
        );
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch (error) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.settingChangeFailed'),
        });
        console.log('  error', error);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-select .v-chip {
    margin: 2px;
  }
  .v-select__selections {
    flex-wrap: wrap;
  }
}
</style>
